import React, { useEffect } from "react";

import {
  TFunction,
  formatFractionDisplay,
  queryIsLoading,
  scrollPage,
  useDairyMethodology,
  useGetDairyCowTypes,
  useGetDairyFeedAdditives,
  useGetDairyFeedTypes,
  useGetDairyManureStorage,
  useGetFootprintingDairyFootprint,
  useGetManureAdditives,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { getUnitDisplayLabel } from "consts";

import { AggregatedDairyFootprint, DAIRY_PRACTICE_SLUG, DAIRY_REFRIGERANTS_ACTIVE } from "models";

import { Card, ListCard } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import DairyRefrigerantsForm from "pages/app/footprinting/components/formFlows/dairyFlow/DairyRefrigerantsForm";
import { FeedAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedAdditiveForm";
import { FeedForageLossForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedForageLossForm";
import { FeedInformationForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedInformationForm";
import { FeedPastureForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedPastureForm";
import { CommercialClassesForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdCommercialClasses";
import { HerdConditionForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdConditionForm";
import { HerdDetailsForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdDetailsForm";
import { HerdOffspringForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdOffspringForm";
import { HerdSizeForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdSizeForm";
import { ManureAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureAdditiveForm";
import { ManureStorageForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureStorageForm";
import { MilkYieldForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MilkYieldForm";
import { MoorlandForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MoorlandForm";
import { DairyCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

/** One refrigerant's review rows; the recovery type is only asked for disposed equipment */
export const refrigerantUsageDataPairs = (t: TFunction, usage: AggregatedDairyFootprint["refrigerantUsage"][number]) =>
  [
    { key: "refrigerantType", value: usage.refrigerantType ?? "" },
    { key: "refrigerantAmount", value: `${usage.amountUsedKg ?? ""} kg` },
    { key: "subapplication", value: usage.subapplication ?? "" },
    { key: "unitCount", value: usage.unitCount?.toString() ?? "" },
    { key: "unitAge", value: usage.unitAge == null ? "" : `${usage.unitAge} years` },
    { key: "equipmentEol", value: usage.equipmentEol == null ? "" : t(`footprinting:radioAnswer.${usage.equipmentEol}`) },
    ...(usage.equipmentEol ? [{ key: "refrigerantRecoveryType", value: usage.refrigerantRecoveryType ?? "" }] : []),
  ].map(({ key, value }) => ({ label: t(`footprintingDairy:dairyRefrigerantsForm.question.${key}`), value }));

interface FootprintingDairyReviewProps {
  year?: number;
}

export const FootprintingDairyReview = ({ year }: FootprintingDairyReviewProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["footprintingDairy", "footprinting", "practices"]);
  const { user } = useUserContext();
  const farmId = user?.farm.id ?? undefined;

  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year);
  const cowTypesQuery = useGetDairyCowTypes({ enabled: true });
  const feedTypesQuery = useGetDairyFeedTypes();
  const feedAdditivesQuery = useGetDairyFeedAdditives();
  const manureStorageQuery = useGetDairyManureStorage();
  const manureAdditivesQuery = useGetManureAdditives();
  const { isCfp } = useDairyMethodology(farmId);

  useEffect(() => {
    scrollPage();
  }, [dairyProgressQuery.data?.footprint?.id]);

  const naviagteToFormPage = (pageKey: string) => {
    navigate("/footprinting/form", { state: { year, formType: "dairy", pageKey } });
  };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`, { ns: "footprinting" }));
  const isLoading = [dairyProgressQuery, cowTypesQuery, feedAdditivesQuery, manureStorageQuery, manureAdditivesQuery].some(queryIsLoading);

  return (
    <>
      <Card className="padding-125">
        <ReviewPageTitle title={t("review.dairy.title")} iconName="klim/cow" isLoading={isLoading} />
        {/* Milk yield */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.milkYield.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(MilkYieldForm.Key) }}
          dataPairs={[
            {
              label: t("reviewAndSubmit.milkYield.milkYield"),
              value: `${dairyProgressQuery.data?.footprint?.milkYieldKgPerCow}`,
            },
            {
              label: t("reviewAndSubmit.milkYield.deliveredOrProduced"),
              value: dairyProgressQuery.data?.footprint?.milkProduction === "produced" ? "produced" : "delivered",
            },
            {
              label: t("reviewAndSubmit.milkYield.fatContent"),
              value: `${formatFractionDisplay(dairyProgressQuery.data?.footprint?.fatContentPct ?? 0)} %`,
            },
            {
              label: t("reviewAndSubmit.milkYield.proteinContent"),
              value: `${formatFractionDisplay(dairyProgressQuery.data?.footprint?.proteinContentPct ?? 0)} %`,
            },
          ]}
        />
        {/* Herd size */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.herdSize.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(HerdSizeForm.Key) }}
          dataPairs={[
            ...(dairyProgressQuery.data?.footprint?.herdSize
              .filter(({ cowCount }) => (cowCount ?? 0) > 0)
              .map((herdSizeInfo) => ({
                label: cowTypesQuery.data?.find((ct) => ct.id === herdSizeInfo.cowTypeId)?.name ?? "",
                value: `${herdSizeInfo.cowCount}`,
              })) ?? []),
            {
              label: t("reviewAndSubmit.herdSize.calves"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.calvesAmount}`,
            },
          ]}
        />
        {/* Herd details */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.herdDetails.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(HerdDetailsForm.Key) }}
          dataPairs={[
            {
              label: t("reviewAndSubmit.herdDetails.cowLosses"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.avgCowLosses}`,
            },
            {
              label: t("reviewAndSubmit.herdDetails.usefulLifespan"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.avgCowUsefulLifeMonths} ${t(
                "dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix"
              )}`,
            },
          ]}
        />
        {/* Weight / Condition */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.wightCondition.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(HerdConditionForm.Key) }}
          dataPairs={[
            {
              label: t("reviewAndSubmit.wightCondition.usedEstimation"),
              value: displayRadioAnswer(dairyProgressQuery.data?.footprint?.herdDetails.cowWeightKnown === "no"),
            },
            {
              label: t("dairyHerdConditionForm.question.cowWeightInput"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.averageCowWeightKg} ${t("dairyHerdConditionForm.question.cowWeightSuffix")}`,
            },
          ]}
        />
        {/* The Offspring */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.offspring.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(HerdOffspringForm.Key) }}
          dataPairs={[
            {
              label: t("dairyHerdOffspringForm.question.matFeedPctInput"),
              value: `${((dairyProgressQuery.data?.footprint?.herdDetails.matFeedPct ?? 0) * 100).toFixed(0)}%`,
            },
            {
              label: t("dairyHerdOffspringForm.question.wholeMilkFeedPctInput"),
              value: `${((dairyProgressQuery.data?.footprint?.herdDetails.wholeMilkFeedPct ?? 0) * 100).toFixed(0)}%`,
            },
            {
              label: t("dairyHerdOffspringForm.question.ageOfFirstCalvingInput"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.ageOfFirstCalvingMonths} ${t(
                "dairyHerdOffspringForm.question.ageOfFirstCalvingSuffix"
              )}`,
            },
            {
              label: t("dairyHerdOffspringForm.question.timeBetweenCalvingInput"),
              value: `${dairyProgressQuery.data?.footprint?.herdDetails.timeBetweenCalvingDays} ${t(
                "dairyHerdOffspringForm.question.timeBetweenCalvingSuffix"
              )}`,
            },
          ]}
        />
        {/* Cow sales */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.cowSales.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(CommercialClassesForm.Key) }}
          dataPairs={[
            {
              label: t("dairyHerdCommercialClassesForm.question.poorConditionInput"),
              value: `${dairyProgressQuery.data?.footprint?.cowSales.cowSalesInPoorCondition}`,
            },
            {
              label: t("dairyHerdCommercialClassesForm.question.lightManufacturingInput"),
              value: `${dairyProgressQuery.data?.footprint?.cowSales.cowSalesForLightManufacturing}`,
            },
            {
              label: t("dairyHerdCommercialClassesForm.question.standardManufacturingInput"),
              value: `${dairyProgressQuery.data?.footprint?.cowSales.cowSalesForStandardManufacturing}`,
            },
            {
              label: t("dairyHerdCommercialClassesForm.question.goodConditionInput"),
              value: `${dairyProgressQuery.data?.footprint?.cowSales.cowSalesInGoodCondition}`,
            },
            {
              label: t("dairyHerdCommercialClassesForm.question.premiumConditionInput"),
              value: `${dairyProgressQuery.data?.footprint?.cowSales.cowSalesInPremiumCondition}`,
            },
          ]}
        />
        {/* Feed pasture */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.feedPasture.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(FeedPastureForm.Key) }}
          dataPairs={[
            {
              label: t("dairyFeedPastureForm.cowPastureRadioQuestion"),
              value: t(`dairyFeedPastureForm.cowPastureOptions.${dairyProgressQuery.data?.footprint?.cowsInPastureActive}`),
            },
            ...(dairyProgressQuery.data?.footprint?.cowsInPastureActive === "yes"
              ? [
                  {
                    label: t("dairyFeedPastureForm.question.cowsInPastureInput"),
                    value: ` ${dairyProgressQuery.data?.footprint?.cowsInPasture ?? 0} ${t("dairyFeedPastureForm.question.cowsInPastureSuffix")}`,
                  },
                  {
                    label: t("dairyFeedPastureForm.question.daysInPastureInput"),
                    value: ` ${dairyProgressQuery.data?.footprint?.daysInPasturePerYear ?? 0} ${t("dairyFeedPastureForm.question.daysInPastureSuffix")}`,
                  },
                  {
                    label: t("dairyFeedPastureForm.question.avgHoursInPastureInput"),
                    value: ` ${dairyProgressQuery.data?.footprint?.avgHoursInPasturePerDay ?? 0} ${t("dairyFeedPastureForm.question.avgHoursInPastureSuffix")}`,
                  },
                ]
              : []),
          ]}
        />
        {/* Feed types / amounts */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.feedTypes.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(FeedInformationForm.Key) }}
          dataPairs={dairyProgressQuery.data?.footprint?.feedInformation.map((feedInfo) => {
            const feedType = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedTypeId);
            const energy = `${feedInfo.energyValue} ${getUnitDisplayLabel(feedInfo.energyUnit)}`;
            return {
              label: feedType?.name ?? "",
              value: feedType?.isCustom
                ? `${energy} · ${t("dairyFeedTypesForm.question.emissionFactor")}: ${feedInfo.emissionFactorKgCo2Eq} ${t(
                    "dairyFeedTypesForm.question.emissionFactorUnit"
                  )}`
                : energy,
            };
          })}
        />
        {/* Feed additives */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.feedAdditives.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(FeedAdditiveForm.Key) }}
          dataPairs={[
            {
              label: t("dairyFeedAdditivesForm.feedAdditivesQuestion"),
              value: t(`dairyFeedAdditivesForm.feedAdditiveOptions.${dairyProgressQuery.data?.footprint?.feedAdditiveActive}`),
            },
            ...(dairyProgressQuery.data?.footprint?.feedAdditiveUsage ?? []).map((feedAdditiveUsage) => ({
              label: t(
                `${DAIRY_PRACTICE_SLUG.FEED_ADDITIVES}.${feedAdditivesQuery.data?.find((fa) => fa.id === feedAdditiveUsage.additiveId)?.translationSlug}`,
                { ns: "dairy" }
              ),
              value: `${feedAdditiveUsage.amount} ${feedAdditiveUsage.amountUnit}`,
            })),
          ]}
        />
        {/* Forage loss */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.forageLoss.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(FeedForageLossForm.Key) }}
          dataPairs={[
            {
              label: t("dairyFeedForageLossForm.forageLossQuestion"),
              value: `${dairyProgressQuery.data?.footprint?.forageLossPct} %`,
            },
          ]}
        />
        {/* Manure storage */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.manureStorage.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(ManureStorageForm.Key) }}
          dataPairs={[
            ...(dairyProgressQuery.data?.footprint?.manureStorageUsage ?? []).map(
              (manureStorageUsage) => ({
                label: manureStorageQuery.data?.find(({ id }) => id === manureStorageUsage.storageId)?.name ?? "Unknown",
                value: `${formatFractionDisplay(manureStorageUsage.amountStoredPct)} %`,
              }),
              {
                label: "Gas tank storage",
                value: `${dairyProgressQuery.data?.footprint?.amountStoredGasTankPct} %`,
              }
            ),
          ]}
        />
        {/* Manure additives */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.manureAdditives.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(ManureAdditiveForm.Key) }}
          dataPairs={[
            {
              label: t("reviewAndSubmit.manureAdditives.sluryAcidifiersUsed"),
              value: displayRadioAnswer((dairyProgressQuery.data?.footprint?.manureAdditiveUsage.length ?? 0) > 0),
            },
            ...(dairyProgressQuery.data?.footprint?.manureAdditiveUsage ?? []).map((manureAdditiveUsage) => ({
              label: manureAdditivesQuery.data?.find(({ id }) => manureAdditiveUsage.additiveId === id)?.name ?? "",
              value: `${manureAdditiveUsage.amountUsedM3}`,
            })),
          ]}
        />
        {/* Refrigerants (CFP only: LfL has no refrigerant inputs) */}
        {isCfp && (
          <ListCard
            className="margin-bottom-100"
            isOutlined
            isLoading={isLoading}
            title={t("footprinting:navigation.dairy.refrigeration")}
            titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(DairyRefrigerantsForm.Key) }}
            dataPairs={[
              {
                label: t("dairyRefrigerantsForm.question.refrigerantsUsed"),
                value: displayRadioAnswer(
                  dairyProgressQuery.data?.footprint?.refrigerantsActive
                    ? dairyProgressQuery.data.footprint.refrigerantsActive === DAIRY_REFRIGERANTS_ACTIVE.YES
                    : null
                ),
                children: (dairyProgressQuery.data?.footprint?.refrigerantUsage ?? []).map((usage, index) => (
                  <ListCard
                    key={usage.id}
                    isSublist
                    isLoading={isLoading}
                    title={t("dairyRefrigerantsForm.question.title") + (index + 1)}
                    dataPairs={refrigerantUsageDataPairs(t, usage)}
                  />
                )),
              },
            ]}
          />
        )}
        {/* Moorland */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("reviewAndSubmit.moorland.cardTitle")}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(MoorlandForm.Key) }}
          dataPairs={[
            {
              label: t("reviewAndSubmit.moorland.moorlandUsed"),
              value: displayRadioAnswer(dairyProgressQuery.data?.footprint?.moorlandActive === "yes"),
            },
            ...(dairyProgressQuery.data?.footprint?.moorlandActive === "yes"
              ? [
                  {
                    label: t("reviewAndSubmit.moorland.fodderArea"),
                    value: `${dairyProgressQuery.data.footprint.fodderProductionAreaHa} ${t("dairyMoorlandForm.question.fodderProductionAreaSuffix")}`,
                  },
                  {
                    label: t("reviewAndSubmit.moorland.averageWaterLevel"),
                    value: `${dairyProgressQuery.data.footprint.annualWaterLevelCm} ${t("dairyMoorlandForm.question.annualWaterLevelSuffix")}`,
                  },
                ]
              : []),
          ]}
        />
        {/* Comment */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("commentForm.title", { ns: "footprinting" })}
          titleButton={{ label: t("review.edit", { ns: "footprinting" }), onClick: () => naviagteToFormPage(DairyCommentForm.Key) }}
          dataPairs={[
            {
              label: t("commentForm.label", { ns: "footprinting" }),
              value: dairyProgressQuery.data?.footprint?.comment || t("commentForm.noComment"),
            },
          ]}
        />
      </Card>
    </>
  );
};
